// Shared helpers for the tabbed landing page.
// Ported from the vanilla prototype (tmp/tabbed (1).js) so the interactive
// panels (Mini Workbench, Estimate) can keep their DOM-driven logic while the
// surrounding sections become React components.

export const ICONS = {
  arrow: 'M5 12h14M13 6l6 6-6 6',
  sparkle: 'M12 3v4M12 17v4M3 12h4M17 12h4M6 6l2.5 2.5M15.5 15.5L18 18M6 18l2.5-2.5M15.5 8.5L18 6',
  check: 'M5 12l5 5 9-12',
  play: 'M6 4l14 8-14 8z',
};

export function icon(d, extra = '') {
  return `<svg class="ic-svg ${extra}" viewBox="0 0 24 24"><path d="${d}"/></svg>`;
}

// Small DOM helper — mirrors the vanilla prototype's `h(tag, attrs, ...children)`.
export function h(tag, attrs = {}, ...children) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') el.className = v;
    else if (k === 'html') el.innerHTML = v;
    else if (k.startsWith('on') && typeof v === 'function') el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (v !== false && v != null) el.setAttribute(k, v);
  }
  for (const c of children.flat()) {
    if (c == null || c === false) continue;
    el.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
  }
  return el;
}

// ---- AWS icon rendering ----
const AWS_ICON_FILES = {};

function inlineAwsIcon(service, size = 28) {
  const bg = {
    ec2:           '#ED7100',
    lambda:        '#ED7100',
    s3:            '#7AA116',
    rds:           '#2E27AD',
    'iam-role':    '#DD344C',
    eventbridge:   '#E7157B',
    alb:           '#8C4FFF',
    nat:           '#8C4FFF',
    'vpc-tile':    '#8C4FFF',
    'subnet-tile': '#8C4FFF',
  }[service] || '#ED7100';

  const glyph = {
    ec2: `<path d="M8 9 L20 9 L23 12 L23 22 L11 22 L8 19 Z M8 9 L11 12 L23 12 M11 12 L11 22" stroke="#fff" stroke-width="1.6" fill="none" stroke-linejoin="round"/>
          <circle cx="14" cy="16" r="0.9" fill="#fff"/><circle cx="17" cy="16" r="0.9" fill="#fff"/><circle cx="20" cy="16" r="0.9" fill="#fff"/>
          <circle cx="14" cy="19" r="0.9" fill="#fff"/><circle cx="17" cy="19" r="0.9" fill="#fff"/><circle cx="20" cy="19" r="0.9" fill="#fff"/>`,
    lambda: `<path d="M10 8 L13 8 L20.5 23 L17.5 23 L15 18 L12.5 23 L9.5 23 L13.5 15.3 Z" fill="#fff"/>`,
    s3: `<path d="M9 11 C9 9.5 12 8.5 16 8.5 C20 8.5 23 9.5 23 11 L22 22 C22 23 19 23.5 16 23.5 C13 23.5 10 23 10 22 Z"
             stroke="#fff" stroke-width="1.5" fill="none" stroke-linejoin="round"/>
         <ellipse cx="16" cy="11" rx="7" ry="1.5" fill="none" stroke="#fff" stroke-width="1.5"/>
         <circle cx="19.5" cy="13.5" r="0.9" fill="#fff"/>`,
    rds: `<ellipse cx="16" cy="10" rx="7" ry="2" stroke="#fff" stroke-width="1.5" fill="none"/>
          <path d="M9 10 L9 22 C9 23.1 12.1 24 16 24 C19.9 24 23 23.1 23 22 L23 10" stroke="#fff" stroke-width="1.5" fill="none"/>
          <path d="M9 14 C9 15.1 12.1 16 16 16 C19.9 16 23 15.1 23 14" stroke="#fff" stroke-width="1.5" fill="none"/>
          <path d="M9 18 C9 19.1 12.1 20 16 20 C19.9 20 23 19.1 23 18" stroke="#fff" stroke-width="1.5" fill="none"/>`,
    'iam-role': `<path d="M16 7 L22 9.5 L22 15 C22 19 19 22 16 24 C13 22 10 19 10 15 L10 9.5 Z" stroke="#fff" stroke-width="1.5" fill="none" stroke-linejoin="round"/>
                 <circle cx="16" cy="14" r="1.8" fill="#fff"/>
                 <path d="M13 19.5 C13 18 14.3 17 16 17 C17.7 17 19 18 19 19.5" stroke="#fff" stroke-width="1.4" fill="none" stroke-linecap="round"/>`,
    eventbridge: `<circle cx="16" cy="16" r="7" stroke="#fff" stroke-width="1.5" fill="none"/>
                  <path d="M13 13 L19 16 L13 19 Z" fill="#fff"/>
                  <circle cx="16" cy="16" r="1.4" fill="${bg}"/>`,
    alb: `<circle cx="16" cy="10" r="2.2" stroke="#fff" stroke-width="1.5" fill="none"/>
          <path d="M16 12.2 L16 16 M16 16 L11 20 M16 16 L21 20" stroke="#fff" stroke-width="1.5" fill="none" stroke-linecap="round"/>
          <circle cx="11" cy="22" r="1.7" stroke="#fff" stroke-width="1.4" fill="none"/>
          <circle cx="21" cy="22" r="1.7" stroke="#fff" stroke-width="1.4" fill="none"/>`,
    nat: `<path d="M8 16 L24 16" stroke="#fff" stroke-width="1.5"/>
          <path d="M12 10 L18 10 L18 14 L20 14 L15 20 L10 14 L12 14 Z" fill="#fff"/>
          <path d="M20 22 L14 22 L14 18 L12 18 L17 12 L22 18 L20 18 Z" fill="#fff" opacity="0.55"/>`,
    'vpc-tile': `<rect x="7" y="9" width="18" height="14" rx="2"
                    stroke="#fff" stroke-width="1.5" fill="none" stroke-dasharray="2.2 1.8"/>
                 <circle cx="16" cy="16" r="2.2" fill="#fff"/>`,
    'subnet-tile': `<rect x="7" y="9" width="18" height="14" rx="2"
                        stroke="#fff" stroke-width="1.4" fill="none"/>
                    <rect x="11" y="13" width="10" height="6" rx="1"
                        stroke="#fff" stroke-width="1.4" fill="none" stroke-dasharray="2 1.4"/>`,
  }[service] || `<text x="16" y="21" font-family="monospace" font-size="14" font-weight="700" text-anchor="middle" fill="#fff">?</text>`;

  return `<svg class="aws-ic" viewBox="0 0 32 32" width="${size}" height="${size}" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
    <rect x="1" y="1" width="30" height="30" rx="5" fill="${bg}"/>
    ${glyph}
    <rect x="1" y="1" width="30" height="30" rx="5" fill="url(#awsGloss)" opacity="0.15" pointer-events="none"/>
  </svg>`;
}

export function awsIcon(service, size = 28) {
  if (AWS_ICON_FILES[service]) {
    const fallback = inlineAwsIcon(service, size).replace(/"/g, '&quot;');
    return `<img class="aws-ic aws-ic-file" src="${AWS_ICON_FILES[service]}"
                 width="${size}" height="${size}" alt="" draggable="false"
                 onerror="this.outerHTML=this.dataset.fb"
                 data-fb="${fallback}"/>`;
  }
  return inlineAwsIcon(service, size);
}

export function ensureAwsDefs() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('awsDefs')) return;
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.id = 'awsDefs';
  svg.setAttribute('width', '0');
  svg.setAttribute('height', '0');
  svg.style.position = 'absolute';
  svg.innerHTML = `<defs><linearGradient id="awsGloss" x1="0" x2="0" y1="0" y2="1">
    <stop offset="0" stop-color="#fff" stop-opacity="0.45"/>
    <stop offset="1" stop-color="#fff" stop-opacity="0"/>
  </linearGradient></defs>`;
  document.body.appendChild(svg);
}

function serviceOf(kind) {
  return ({ lambdaRole: 'iam-role' })[kind] || kind;
}

// ---- Tabbed hero metadata ----
export const TAB_META = {
  design: {
    title: 'Design',
    tagline: 'Compose infrastructure like a diagram.',
    summary: 'Drag AWS, Azure, or GCP resources onto the canvas. See usage cost, quota, policy violations in a single frame!',
  },
  code: {
    title: 'Code',
    tagline: 'Every canvas change is a clean IaC diff.',
    summary: "Bidirectional sync. Import what's running, export what you design — cleanly, every time.",
  },
  estimate: {
    title: 'Estimate',
    tagline: 'See monthly cost before anyone opens a PR.',
    summary: 'Projected, direct, and implied — per resource. Commitment-plan aware.',
  },
  review: {
    title: 'Review',
    tagline: 'Policy checks, cost diff, and a PR ready to merge.',
    summary: 'OPA rules run on the design and on the live cloud. Evidence attached to every change.',
  },
};

// ---- Workbench catalog ----
export const WB_PROTO = {
  lambda:      { cat: 'COMPUTE', service: 'lambda',      label: 'Lambda',      short: 'lambda_fn', cost: 0.80 },
  ec2:         { cat: 'COMPUTE', service: 'ec2',         label: 'EC2',         short: 'server',    cost: 5.40 },
  s3:          { cat: 'STORAGE', service: 's3',          label: 'S3',          short: 'bucket',    cost: 0.60 },
  rds:         { cat: 'STORAGE', service: 'rds',         label: 'RDS',         short: 'db',        cost: 14.20 },
  lambdaRole:  { cat: 'IAM',     service: 'iam-role',    label: 'IAM Role',    short: 'role',      cost: 0 },
  eventbridge: { cat: 'EVENTS',  service: 'eventbridge', label: 'EventBridge', short: 'rule',      cost: 1.00 },
  alb:         { cat: 'NETWORK', service: 'alb',         label: 'ALB',         short: 'alb',       cost: 21.60 },
};

export const WB_DRAWER = [
  { cat: 'COMPUTE', count: 2, items: [
    { k: 'ec2',    label: 'EC2',    addable: 'ec2',    service: 'ec2' },
    { k: 'lambda', label: 'Lambda', addable: 'lambda', service: 'lambda' },
  ] },
  { cat: 'STORAGE', count: 2, items: [
    { k: 's3',  label: 'S3',  addable: 's3',  service: 's3' },
    { k: 'rds', label: 'RDS', addable: 'rds', service: 'rds' },
  ] },
  { cat: 'NETWORKING', count: 4, items: [
    { k: 'alb',    label: 'ALB',    service: 'alb',         disabled: true },
    { k: 'nat',    label: 'NAT GW', service: 'nat',         disabled: true },
    { k: 'vpc',    label: 'VPC',    service: 'vpc-tile',    disabled: true },
    { k: 'subnet', label: 'Subnet', service: 'subnet-tile', disabled: true },
  ] },
];

// ---- Mini Workbench init (wires up the DOM inside a root node) ----
export function initWorkbench(wb) {
  ensureAwsDefs();

  const state = {
    containers: [
      { id: 'aws', type: 'cloud', label: 'development',      x: 20, y: 24, w: 920, h: 430 },
      { id: 'vpc', type: 'vpc',   label: 'vpc · 10.0.0.0/16', x: 52, y: 66, w: 720, h: 380 },
    ],
    nodes: [
      { id: 'a1', kind: 'alb', label: 'alb',            cont: 'vpc', x: 280, y: 150, cost: 21.60 },
      { id: 's1', kind: 'ec2', label: 'ib_test_server', cont: 'vpc', x: 520, y: 220, cost:  5.40 },
      { id: 'd1', kind: 'rds', label: 'analytics_db',   cont: 'vpc', x: 380, y: 340, cost: 14.20 },
    ],
    edges: [
      ['a1', 's1', { dashed: true }],
      ['s1', 'd1', { dashed: true }],
    ],
    nextId: 20,
  };

  const canvas     = wb.querySelector('#wbCanvas');
  const edgesSvg   = wb.querySelector('#wbEdges');
  const drawerList = wb.querySelector('#wbDrawerList');
  const closeBtn   = wb.querySelector('.wb-drawer-close');
  const tick       = wb.querySelector('#wbTicker');
  const $proj      = wb.querySelector('#wbProj');
  const $annual    = wb.querySelector('#wbAnnual');
  const $direct    = wb.querySelector('#wbDirect');
  const $implied   = wb.querySelector('#wbImplied');
  const $count     = wb.querySelector('#wbCount');

  if (!canvas) return () => {};

  function renderDrawer() {
    if (!drawerList) return;
    drawerList.innerHTML = '';
    WB_DRAWER.forEach((g) => {
      const sec = h('section', { class: 'wb-grp' });
      const head = h('header', { class: 'wb-grp-head' },
        h('span', { class: 'wb-grp-caret' }, '▾'),
        h('span', { class: 'wb-grp-label' }, g.cat),
        h('span', { class: 'wb-grp-count' }, String(g.count))
      );
      head.addEventListener('click', () => sec.classList.toggle('is-closed'));
      const grid = h('div', { class: 'wb-grp-grid' });
      g.items.forEach((it) => {
        const isDisabled = !!it.disabled;
        const tile = h('button', {
          class: 'wb-tile'
            + (it.addable ? ' is-addable' : '')
            + (isDisabled ? ' is-disabled' : ''),
          'data-kind': it.addable || '',
          type: 'button',
          draggable: it.addable ? 'true' : 'false',
          disabled: isDisabled,
          title: isDisabled
            ? it.label.replace('\n', ' ') + ' — coming soon'
            : it.label.replace('\n', ' '),
        });
        tile.innerHTML = `
          <span class="wb-tile-icon">${awsIcon(it.service || it.k, 32)}</span>
          <span class="wb-tile-label">${it.label.replace('\n', '<br>')}</span>
        `;
        if (it.addable && !isDisabled) {
          tile.addEventListener('click', () => addNode(it.addable));
          tile.addEventListener('dragstart', (e) => {
            e.dataTransfer.setData('text/plain', it.addable);
            e.dataTransfer.effectAllowed = 'copy';
          });
        }
        grid.appendChild(tile);
      });
      sec.appendChild(head);
      sec.appendChild(grid);
      drawerList.appendChild(sec);
    });
  }

  function render() {
    canvas.querySelectorAll('.wb-cont, .wb-node').forEach((el) => el.remove());

    for (const c of state.containers) {
      const el = h('div', {
        class: `wb-cont wb-cont-${c.type}`,
        style: `left:${c.x}px; top:${c.y}px; width:${c.w}px; height:${c.h}px;`,
      },
        h('span', { class: 'wb-cont-label' },
          h('span', { class: 'wb-cont-badge' }, c.type === 'cloud' ? 'aws' : (c.type === 'vpc' ? '▦' : '◧')),
          c.label
        )
      );
      canvas.appendChild(el);
    }

    for (const n of state.nodes) {
      const svc = serviceOf(n.kind);
      const el = h('div', {
        class: 'wb-node' + (n.fresh ? ' is-new' : ''),
        'data-id': n.id,
        style: `left:${n.x}px; top:${n.y}px;`,
      });
      el.innerHTML = `
        <span class="wb-node-ic">${awsIcon(svc, 34)}</span>
        <span class="wb-node-label">${n.label}</span>
      `;
      canvas.appendChild(el);
      n.fresh = false;
    }

    requestAnimationFrame(drawEdges);
    updateTicker();
  }

  function drawEdges() {
    if (!edgesSvg) return;
    const rect = canvas.getBoundingClientRect();
    edgesSvg.setAttribute('viewBox', `0 0 ${rect.width} ${rect.height}`);
    edgesSvg.style.width = rect.width + 'px';
    edgesSvg.style.height = rect.height + 'px';
    edgesSvg.innerHTML = '';
    const defs = document.createElementNS('http://www.w3.org/2000/svg', 'defs');
    defs.innerHTML = `<filter id="glow" x="-30%" y="-30%" width="160%" height="160%">
      <feGaussianBlur stdDeviation="1.2" result="b"/>
      <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
    </filter>`;
    edgesSvg.appendChild(defs);

    for (const [a, b, opt = {}] of state.edges) {
      const na = state.nodes.find((n) => n.id === a);
      const nb = state.nodes.find((n) => n.id === b);
      if (!na || !nb) continue;

      const aIcon = canvas.querySelector(`.wb-node[data-id="${a}"] .wb-node-ic`);
      const bIcon = canvas.querySelector(`.wb-node[data-id="${b}"] .wb-node-ic`);
      if (!aIcon || !bIcon) continue;
      const ar = aIcon.getBoundingClientRect();
      const br = bIcon.getBoundingClientRect();
      const cr = canvas.getBoundingClientRect();

      const aCx = ar.left + ar.width / 2 - cr.left;
      const bCx = br.left + br.width / 2 - cr.left;
      const goingRight = bCx >= aCx;
      const ax = goingRight ? ar.right - cr.left : ar.left  - cr.left;
      const bx = goingRight ? br.left  - cr.left : br.right - cr.left;
      const ay = ar.top + ar.height / 2 - cr.top;
      const by = br.top + br.height / 2 - cr.top;

      const midx = (ax + bx) / 2;
      const r = 6;
      const dx1 = goingRight ? Math.min(r, Math.abs(midx - ax)) : -Math.min(r, Math.abs(midx - ax));
      const dy  = by > ay ? Math.min(r, Math.abs(by - ay)) : -Math.min(r, Math.abs(by - ay));
      const dx2 = goingRight ? Math.min(r, Math.abs(bx - midx)) : -Math.min(r, Math.abs(bx - midx));

      let d;
      if (Math.abs(ay - by) < 1) {
        d = `M ${ax} ${ay} L ${bx} ${by}`;
      } else {
        d = `M ${ax} ${ay}
             L ${midx - dx1} ${ay}
             Q ${midx} ${ay} ${midx} ${ay + dy}
             L ${midx} ${by - dy}
             Q ${midx} ${by} ${midx + dx2} ${by}
             L ${bx} ${by}`;
      }
      const p = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      p.setAttribute('d', d);
      p.setAttribute('class', 'wb-edge' + (opt.dashed ? ' is-dashed' : ''));
      p.setAttribute('filter', 'url(#glow)');
      edgesSvg.appendChild(p);

      if (opt.label) {
        const fo = document.createElementNS('http://www.w3.org/2000/svg', 'foreignObject');
        const pillW = Math.max(50, opt.label.length * 6.8);
        fo.setAttribute('x', midx - pillW / 2);
        fo.setAttribute('y', (ay + by) / 2 - 10);
        fo.setAttribute('width', pillW);
        fo.setAttribute('height', 20);
        fo.innerHTML = `<div xmlns="http://www.w3.org/1999/xhtml" class="wb-edge-pill">${opt.label}</div>`;
        edgesSvg.appendChild(fo);
      }
    }
  }

  let lastTotal = null;
  function updateTicker() {
    if (!tick) return;
    const direct = state.nodes.reduce((s, n) => s + (n.cost || 0), 0);
    const implied = direct * 0.015;
    const projected = direct + implied;
    const annual = projected * 12;
    const fmt = (v) => '$' + v.toFixed(2);

    if ($direct)  $direct.textContent = fmt(direct);
    if ($implied) $implied.textContent = '+' + fmt(implied);
    if ($annual)  $annual.textContent = fmt(annual);
    if ($count)   $count.textContent = state.nodes.length;
    if ($proj)    $proj.innerHTML = fmt(projected) + '/<i>mo</i>';

    if (lastTotal !== null && Math.abs(projected - lastTotal) > 0.01) {
      const dir = projected > lastTotal ? 'up' : 'down';
      tick.classList.remove('is-up', 'is-down');
      void tick.offsetWidth;
      tick.classList.add('is-' + dir);
      setTimeout(() => tick.classList.remove('is-' + dir), 900);
    }
    lastTotal = projected;
  }

  function addNode(kind, x, y) {
    const p = WB_PROTO[kind];
    if (!p) return;
    const id = 'x' + state.nextId++;
    if (x == null) { x = 320 + Math.random() * 120; y = 264 + Math.random() * 40; }
    const node = {
      id, kind,
      label: `${p.short}_${state.nextId - 1}`,
      cont: 'vpc',
      x, y,
      cost: p.cost,
      fresh: true,
    };
    state.nodes.push(node);
    const near = state.nodes.filter((n) => n.id !== id).reduce((a, b) => {
      const da = Math.hypot(a.x - x, a.y - y);
      const db = Math.hypot(b.x - x, b.y - y);
      return db < da ? b : a;
    }, state.nodes[0]);
    if (near && near.id !== id) state.edges.push([near.id, id, {}]);
    render();
  }

  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; };
  const onDrop = (e) => {
    e.preventDefault();
    const kind = e.dataTransfer.getData('text/plain');
    if (!kind) return;
    const rect = canvas.getBoundingClientRect();
    addNode(kind, e.clientX - rect.left - 48, e.clientY - rect.top - 20);
  };
  canvas.addEventListener('dragover', onDragOver);
  canvas.addEventListener('drop', onDrop);

  const onCloseDrawer = () => {
    wb.classList.toggle('is-drawer-closed');
    requestAnimationFrame(drawEdges);
  };
  closeBtn?.addEventListener('click', onCloseDrawer);

  const onPointerDown = (e) => {
    const el = e.target.closest('.wb-node');
    if (!el) return;
    const node = state.nodes.find((n) => n.id === el.dataset.id);
    if (!node) return;
    const sx = e.clientX, sy = e.clientY;
    const startX = node.x, startY = node.y;
    el.setPointerCapture(e.pointerId);
    el.classList.add('is-dragging');
    function move(ev) {
      node.x = startX + (ev.clientX - sx);
      node.y = startY + (ev.clientY - sy);
      el.style.left = node.x + 'px';
      el.style.top  = node.y + 'px';
      drawEdges();
    }
    function up() {
      el.classList.remove('is-dragging');
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    }
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    e.preventDefault();
  };
  canvas.addEventListener('pointerdown', onPointerDown);

  const tabs = Array.from(wb.querySelectorAll('.wb-tab'));
  const onTabClick = (e) => {
    tabs.forEach((x) => x.classList.toggle('is-active', x === e.currentTarget));
  };
  tabs.forEach((t) => t.addEventListener('click', onTabClick));

  const onResize = () => drawEdges();
  window.addEventListener('resize', onResize);

  renderDrawer();
  render();

  return () => {
    canvas.removeEventListener('dragover', onDragOver);
    canvas.removeEventListener('drop', onDrop);
    canvas.removeEventListener('pointerdown', onPointerDown);
    closeBtn?.removeEventListener('click', onCloseDrawer);
    tabs.forEach((t) => t.removeEventListener('click', onTabClick));
    window.removeEventListener('resize', onResize);
  };
}

// ---- Estimate panel interactive init ----
export function initEstimate(panel) {
  const EC2_TYPES = { 't3.medium': 15.18, 't3.large': 27.61 };
  const S3_STEPS = [10, 20, 30, 40];
  const S3_PRICE_PER_GB = 0.023;
  const LAMBDA_INVOKES = [100000, 500000, 1000000, 2000000];
  const LAMBDA_PRICE_PER_M = 0.20;

  const FIXED   = { alb: 21.60, rds: 28.50 };
  const IMPLIED = { alb: 0.32, s3: 0.18, lambda: 0.03 };

  const state = { ec2Type: 't3.medium', s3Gb: 10, lambdaInvokes: 1000000 };

  const ec2Direct    = () => EC2_TYPES[state.ec2Type];
  const s3Direct     = () => +(state.s3Gb * S3_PRICE_PER_GB).toFixed(2);
  const lambdaDirect = () => +((state.lambdaInvokes / 1000000) * LAMBDA_PRICE_PER_M).toFixed(2);

  function totals() {
    const direct  = FIXED.alb + ec2Direct() + s3Direct() + FIXED.rds + lambdaDirect();
    const implied = IMPLIED.alb + IMPLIED.s3 + IMPLIED.lambda;
    const total   = direct + implied;
    return {
      direct: direct.toFixed(2),
      implied: implied.toFixed(2),
      total: total.toFixed(2),
      annual: (total * 12).toFixed(2),
    };
  }
  const fmt = (n) => '$' + (+n).toFixed(2);
  const fmtK = (n) => {
    if (n >= 1000000) return (n / 1000000) + 'M';
    if (n >= 1000)    return (n / 1000) + 'K';
    return String(n);
  };

  const $ = (sel) => panel.querySelector(sel);

  function render() {
    const ec2D = ec2Direct();
    $('[data-k="ec2Direct"]').textContent = fmt(ec2D);
    $('[data-k="ec2Total"]').textContent  = fmt(ec2D);

    const s3D = s3Direct();
    $('[data-k="s3Label"]').textContent  = state.s3Gb + ' GB';
    $('[data-k="s3Direct"]').textContent = fmt(s3D);
    $('[data-k="s3Total"]').textContent  = fmt(s3D + IMPLIED.s3);

    const lD = lambdaDirect();
    $('[data-k="lambdaLabel"]').textContent  = fmtK(state.lambdaInvokes);
    $('[data-k="lambdaDirect"]').textContent = fmt(lD);
    $('[data-k="lambdaTotal"]').textContent  = fmt(lD + IMPLIED.lambda);

    const t = totals();
    $('[data-k="total"]').innerHTML    = '$' + t.total + '<span class="tp-est-unit">/mo</span>';
    $('[data-k="direct"]').textContent  = '$' + t.direct;
    $('[data-k="implied"]').textContent = '+$' + t.implied;
    $('[data-k="annual"]').textContent  = '$' + t.annual;

    const ceil = $('[data-k="ceiling"]');
    const over = +t.total > 100;
    ceil.textContent = over ? `⚠ over $100/mo ($${t.total})` : `✓ under $100/mo ceiling`;
    ceil.classList.toggle('is-ok', !over);
    ceil.classList.toggle('is-warn', over);

    panel.querySelectorAll('.tp-row-cell.is-total, .tp-est-value').forEach((el) => {
      el.classList.remove('is-pulse'); void el.offsetWidth; el.classList.add('is-pulse');
    });
  }

  const ec2Select = $('[data-k="ec2Type"]');
  const onEc2 = (e) => { state.ec2Type = e.target.value; render(); };
  ec2Select?.addEventListener('change', onEc2);

  const s3Slider = $('[data-k="s3Step"]');
  const onS3 = (e) => { state.s3Gb = S3_STEPS[+e.target.value] || 10; render(); };
  s3Slider?.addEventListener('input', onS3);

  const lambdaPrev = $('[data-k="lambdaPrev"]');
  const onLambdaPrev = () => {
    const i = LAMBDA_INVOKES.indexOf(state.lambdaInvokes);
    state.lambdaInvokes = LAMBDA_INVOKES[Math.max(0, i - 1)];
    render();
  };
  lambdaPrev?.addEventListener('click', onLambdaPrev);

  const lambdaNext = $('[data-k="lambdaNext"]');
  const onLambdaNext = () => {
    const i = LAMBDA_INVOKES.indexOf(state.lambdaInvokes);
    state.lambdaInvokes = LAMBDA_INVOKES[Math.min(LAMBDA_INVOKES.length - 1, i + 1)];
    render();
  };
  lambdaNext?.addEventListener('click', onLambdaNext);

  requestAnimationFrame(render);

  return () => {
    ec2Select?.removeEventListener('change', onEc2);
    s3Slider?.removeEventListener('input', onS3);
    lambdaPrev?.removeEventListener('click', onLambdaPrev);
    lambdaNext?.removeEventListener('click', onLambdaNext);
  };
}

// ---- Reveal-on-scroll observer (once, subtle) ----
export function initReveal(root) {
  if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') return () => {};
  const vh = window.innerHeight;
  const io = new IntersectionObserver((entries) => {
    entries.forEach((e) => {
      if (e.isIntersecting) {
        e.target.classList.add('is-in');
        io.unobserve(e.target);
      }
    });
  }, { threshold: 0.12, rootMargin: '0px 0px -5% 0px' });

  const scope = root || document;
  scope.querySelectorAll('.reveal').forEach((el) => {
    const r = el.getBoundingClientRect();
    if (r.top > vh * 0.6) {
      el.classList.add('pending');
      io.observe(el);
    }
  });
  return () => io.disconnect();
}
